import React from "react";
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { HOMEROOM_ASSIGN_SUBJECTS_MSG, HOMEROOM_MIN_SUBJECTS } from "../../utils/homeroomAssignSubjects";

/**
 * Class + subject picks when assigning a form / class teacher.
 */
export default function HomeroomAssignSubjectsFields({
    homeroomLabel,
    classOptions = [],
    classId,
    onClassChange,
    classDisabled = false,
    subjects = [],
    subjectsLoading = false,
    selectedSubjectIds = [],
    onToggleSubject,
    showClassSelect = true,
    currentTeacherId = "",
}) {
    const selectedSet = new Set(selectedSubjectIds);
    const count = selectedSet.size;

    return (
        <Stack spacing={2}>
            {showClassSelect ? (
                <FormControl fullWidth required disabled={classDisabled}>
                    <InputLabel>Class</InputLabel>
                    <Select
                        label="Class"
                        value={classId || ""}
                        onChange={(e) => onClassChange?.(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>Select class</em>
                        </MenuItem>
                        {classOptions.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                                {c.name}
                                {c.currentTeacherName ? ` — ${c.currentTeacherName}` : ""}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>Homeroom class this teacher is responsible for.</FormHelperText>
                </FormControl>
            ) : null}

            <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Subjects to teach in this class
                    </Typography>
                    <Chip
                        size="small"
                        label={`${count} selected`}
                        color={count >= HOMEROOM_MIN_SUBJECTS ? "success" : "warning"}
                        variant="outlined"
                    />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Select which subjects {homeroomLabel || "this teacher"} will teach in this class. At least{" "}
                    {HOMEROOM_MIN_SUBJECTS} required.
                </Typography>
                {!classId ? (
                    <Alert severity="info">Choose a class first to see its subjects.</Alert>
                ) : subjectsLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Loading subjects…
                        </Typography>
                    </Box>
                ) : subjects.length === 0 ? (
                    <Alert severity="warning">
                        This class has no subjects yet. Add subjects under Classes → Add subjects, then assign them
                        here.
                    </Alert>
                ) : (
                    <FormGroup
                        sx={{
                            maxHeight: 220,
                            overflow: "auto",
                            border: (t) => `1px solid ${t.palette.divider}`,
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.5,
                        }}
                    >
                        {subjects.map((sub) => {
                            const sid = String(sub._id || sub.id);
                            const assignedTo = sub.teacherId || sub.teacher?._id || sub.teacher?.id;
                            const takenByOther =
                                assignedTo &&
                                String(assignedTo) !== String(currentTeacherId) &&
                                !selectedSet.has(sid);
                            const otherName =
                                sub.teacher?.name || sub.teacherName || "another teacher";
                            return (
                                <FormControlLabel
                                    key={sid}
                                    control={
                                        <Checkbox
                                            checked={selectedSet.has(sid)}
                                            onChange={() => onToggleSubject?.(sid)}
                                            disabled={takenByOther}
                                        />
                                    }
                                    label={
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <span>{sub.subName}</span>
                                            {takenByOther ? (
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    ({otherName})
                                                </Typography>
                                            ) : null}
                                        </Stack>
                                    }
                                />
                            );
                        })}
                    </FormGroup>
                )}
                {classId && subjects.length > 0 && count < HOMEROOM_MIN_SUBJECTS ? (
                    <FormHelperText error>{HOMEROOM_ASSIGN_SUBJECTS_MSG}</FormHelperText>
                ) : null}
            </Box>
        </Stack>
    );
}
